import { Maybe } from "~generated/graphqlTypes";
import {
  InsideMapsCamera_InsideMapsCameraFragment,
  InsideMapsTextureSwaps_InsideMapsTextureSwapsFragment,
} from "./InsideMaps.generated";

// Default camera when no location-specific camera is found
export const DEFAULT_CAMERA_NAME = "KitchenView" as const;

/** Type for alternate camera (nested cameras without their own alternateCameras) */
export type AlternateCamera = InsideMapsCamera_InsideMapsCameraFragment["alternateCameras"][number];

/**
 * Data structure for InsideMaps iframe rendering.
 * This is built from the pre-computed `InsideMapsTextureSwaps` API response.
 */
export type InsideMapsData = {
  /** The InsideMaps project ID (used for iframe URL) */
  insideMapsProjectId: string | null | undefined;
  /** Pre-computed texture swaps keyed by camera name */
  insideMapsTextureInputs: Record<string, { part: string; texture: string }[]>;
  /** All camera configs from the API */
  cameras: InsideMapsCamera_InsideMapsCameraFragment[];
  /** The default room location id for InsideMaps */
  defaultInsideMapsRoomLocationId?: string;
};

/**
 * Build the InsideMapsData structure from the API response.
 */
export function buildInsideMapsData(
  textureSwaps: Maybe<InsideMapsTextureSwaps_InsideMapsTextureSwapsFragment>,
  defaultInsideMapsRoomLocationId?: string,
): InsideMapsData {
  const cameras = textureSwaps?.cameras ?? [];
  const projectId = textureSwaps?.projectId;

  const textureInputs = (textureSwaps?.textureSwapsByCamera ?? []).keyBy(
    (cs) => cs.cameraName,
    (cs) => cs.textureSwaps.map(({ part, texture }) => ({ part, texture })),
  );

  // Log debug slots when available
  if (textureSwaps?.debugSlots) {
    console.group("InsideMaps Debug Slots");
    for (const slot of textureSwaps.debugSlots) {
      console.log(slot.message);
    }
    console.groupEnd();
  }

  return {
    insideMapsProjectId: projectId,
    insideMapsTextureInputs: textureInputs,
    cameras,
    defaultInsideMapsRoomLocationId,
  };
}
